import { ImageRotationDegrees, photoTypeRegex, videoTypeRegex } from '@eigtech/documents-types'
import { DocumentV2WithTags, useGetDocumentUrl } from '@eigtech/ui-shared-documents'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { Button, ButtonProps } from '@eigtech/ui/components/button'
import { ButtonGroup } from '@eigtech/ui/components/button-group'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@eigtech/ui/components/carousel'
import { Popover, PopoverContent, PopoverTrigger } from '@eigtech/ui/components/popover'
import { Spinner } from '@eigtech/ui/components/spinner'
import { cn } from '@eigtech/ui/lib/utils'
import { type EmblaEventCallbackType } from 'embla-carousel'
import { ExternalLink, Info, RotateCcw, RotateCw, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { DocumentPropertyList } from '../DocumentCard'
import { MediaPreview } from '../MediaPreview'
import { useRotatePhotoState } from '../useRotatePhotoState'

export type MediaCarouselProps = {
  className?: string
  documents?: DocumentV2WithTags[]
  getDocuments?: () => DocumentV2WithTags[] | undefined
  initialDocument?: DocumentV2WithTags
}

const getInitialIndex = (
  initialDocument?: DocumentV2WithTags,
  documents?: DocumentV2WithTags[]
) => {
  const initialIndex = (documents ?? []).findIndex(
    (document) => document.id === initialDocument?.id
  )

  if (initialIndex > 0) {
    return initialIndex
  }

  return 0
}

export function MediaCarousel({
  className,
  documents: propsDocuments,
  getDocuments,
  initialDocument,
}: MediaCarouselProps) {
  const [documents, setDocuments] = useState(propsDocuments ?? getDocuments?.())

  useEffect(() => {
    const documents = propsDocuments ?? getDocuments?.()
    setDocuments(documents)

    const index = getInitialIndex(initialDocument, documents)

    setInitialIndex(index)
    setCurrentIndex(index)
  }, [getDocuments, initialDocument, propsDocuments])

  const [initialIndex, setInitialIndex] = useState(getInitialIndex(initialDocument, documents))
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  const [api, setApi] = useState<CarouselApi>()

  useEffect(() => {
    if (!api) {
      return
    }

    const onSelect: EmblaEventCallbackType<'select'> = (__data, event) => {
      setCurrentIndex(event.detail.targetSnap)
    }

    api.on('select', onSelect)

    return () => {
      api.off('select', onSelect)
    }
  }, [api])

  const [rotationDegrees, setRotationDegrees] = useState<Record<string, 0 | ImageRotationDegrees>>(
    {}
  )

  if (!documents) {
    return (
      <div
        className="my-4 flex items-center justify-center"
        data-slot="media-carousel-loading-media"
      >
        <Spinner className="text-primary size-60" />
      </div>
    )
  }

  if (!documents.length) {
    return (
      <div className="my-4 flex items-center justify-center">
        <Alert data-slot="media-carousel-no-media" variant="warning">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>No media found</AlertTitle>
          </AlertContent>
        </Alert>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <Carousel
        className={cn('', className)}
        opts={{
          align: 'center',
          loop: true,
          startSnap: initialIndex,
        }}
        setApi={setApi}
      >
        <CarouselContent>
          {documents.map((media) => (
            <CarouselItem key={media.id}>
              <div className="relative flex aspect-square items-center justify-center">
                <MediaPreview
                  className={cn(
                    'w-full',
                    media.filename.match(photoTypeRegex) && 'rotate-(--media-rotate) h-full'
                  )}
                  document={media}
                  style={
                    {
                      '--media-rotate': `${rotationDegrees[media.id] ?? 0}deg`,
                    } as React.CSSProperties
                  }
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>

      <div className="flex w-full items-center justify-center">
        {documents.map((media, index) => (
          <div
            key={media.id}
            className={cn('flex flex-col items-center gap-4', index !== currentIndex && 'hidden')}
          >
            <div
              className={cn(
                'flex flex-col items-center gap-4',
                media.filename.match(videoTypeRegex) && 'opacity-0'
              )}
            >
              <MediaRotate
                document={media}
                rotationDegrees={rotationDegrees[media.id] ?? 0}
                setRotationDegrees={(degrees) => {
                  setRotationDegrees((current) => ({ ...current, [media.id]: degrees }))
                }}
              />
            </div>
            <MediaInfo {...media} />
          </div>
        ))}
      </div>
    </div>
  )
}

function MediaRotate({
  document,
  rotationDegrees,
  setRotationDegrees,
}: {
  document: DocumentV2WithTags
  rotationDegrees: 0 | ImageRotationDegrees
  setRotationDegrees: (degrees: 0 | ImageRotationDegrees) => void
}) {
  const { isFetching } = useGetDocumentUrl({
    entityId: document.entityId,
    documentId: document.id,
  })

  const { saveRotateEnabled, saveRotate, rotateLeft, rotateRight, isPending, isError } =
    useRotatePhotoState(document, { rotationDegrees, setRotationDegrees })

  return (
    <>
      {isError && (
        <Alert
          data-document-id={document.id}
          data-slot="media-carousel-rotate-error"
          variant="destructive"
        >
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>Could not save rotation</AlertTitle>
            <AlertDescription>
              Something went wrong Please try again. If the issue persists, contact EIG Tech
              Support.
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}

      <ButtonGroup size="sm" variant="outline">
        <Button
          data-document-id={document.id}
          data-slot="media-carousel-rotate-left"
          disabled={!!document.filename.match(videoTypeRegex)}
          onClick={rotateLeft}
        >
          <RotateCcw /> Rotate Left
        </Button>
        <Button
          data-document-id={document.id}
          data-slot="media-carousel-save-rotation"
          disabled={!saveRotateEnabled || !!document.filename.match(videoTypeRegex)}
          isLoading={isPending || isFetching}
          variant="primary"
          onClick={saveRotate}
        >
          Save
        </Button>
        <Button
          data-document-id={document.id}
          data-slot="media-carousel-rotate-right"
          disabled={!!document.filename.match(videoTypeRegex)}
          onClick={rotateRight}
        >
          Rotate Right <RotateCw />
        </Button>
      </ButtonGroup>
    </>
  )
}

function MediaInfo(document: DocumentV2WithTags) {
  const { id, entityId, filename } = document
  const { data: url } = useGetDocumentUrl({ entityId, documentId: id })

  const buttonProps = {
    size: 'sm',
    variant: 'ghost',
  } satisfies ButtonProps

  return (
    <div className="flex flex-row items-center gap-2">
      <span className="font-bold">{filename ?? '(No filename)'}</span>

      <div className="flex flex-row items-center">
        <Popover>
          <PopoverTrigger
            render={
              <Button
                {...buttonProps}
                aria-label="View more info"
                className="media-carousel-view-more-info"
                data-document-id={document.id}
                mode="icon"
              />
            }
          >
            <Info />
          </PopoverTrigger>

          <PopoverContent
            className="max-h-[calc(100vh-40rem)] w-full max-w-xl"
            positionerClassName="z-50"
            side="top"
          >
            <h4 className="mb-4 font-bold">Document Information</h4>
            <DocumentPropertyList document={document} entityId={entityId} />
          </PopoverContent>
        </Popover>

        <Button
          {...buttonProps}
          aria-label="Download"
          className="media-carousel-download"
          data-document-id={document.id}
          mode="icon"
          render={<a href={url} target={document.id} />}
        >
          <ExternalLink />
        </Button>
      </div>
    </div>
  )
}
